import React, { useState, useEffect, useMemo } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { ChevronLeft, ChevronRight, CalendarDays } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MONTHS_TR = ['Ocak', 'Şubat', 'Mart', 'Nisan', 'Mayıs', 'Haziran', 'Temmuz', 'Ağustos', 'Eylül', 'Ekim', 'Kasım', 'Aralık'];
const DAYS_TR = ['Pzt', 'Sal', 'Çar', 'Per', 'Cum', 'Cmt', 'Paz'];

const LEAVE_STYLES = {
  yillik: { label: 'Yıllık', chip: 'bg-emerald-100 text-emerald-700 border-emerald-200', dot: 'bg-emerald-500' },
  raporlu: { label: 'Raporlu', chip: 'bg-rose-100 text-rose-700 border-rose-200', dot: 'bg-rose-500' },
  ucretsiz: { label: 'Ücretsiz', chip: 'bg-slate-200 text-slate-700 border-slate-300', dot: 'bg-slate-500' },
  haftalik: { label: 'Haftalık İzin', chip: 'bg-blue-100 text-blue-700 border-blue-200', dot: 'bg-blue-500' },
  diger: { label: 'Diğer', chip: 'bg-amber-100 text-amber-700 border-amber-200', dot: 'bg-amber-500' },
};
const styleFor = (t) => LEAVE_STYLES[t] || LEAVE_STYLES.diger;

const authHeader = () => {
  const t = localStorage.getItem('token');
  return t ? { Authorization: `Bearer ${t}` } : {};
};

const pad = (n) => String(n).padStart(2, '0');

const LeaveCalendarModal = ({ open, onClose, personnelList = [] }) => {
  const now = new Date();
  const [monthStr, setMonthStr] = useState(`${now.getFullYear()}-${pad(now.getMonth() + 1)}`);
  const [leaves, setLeaves] = useState([]);
  const [loading, setLoading] = useState(false);

  const nameMap = useMemo(() => {
    const m = {};
    (personnelList || []).forEach(p => { m[p.id] = p.name; });
    return m;
  }, [personnelList]);

  const fetchLeaves = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/personnel-leaves`, { headers: authHeader() });
      setLeaves(res.data || []);
    } catch (e) {
      setLeaves([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) fetchLeaves();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const [year, month] = monthStr.split('-').map(Number); // month 1-12

  const shiftMonth = (delta) => {
    const d = new Date(year, month - 1 + delta, 1);
    setMonthStr(`${d.getFullYear()}-${pad(d.getMonth() + 1)}`);
  };

  // date(YYYY-MM-DD) -> [{name, leave_type, notes}]
  const dayMap = useMemo(() => {
    const map = {};
    const monthPrefix = `${year}-${pad(month)}`;
    (leaves || []).forEach(lv => {
      if (lv.approved === false) return;
      const s = lv.start_date, e = lv.end_date || lv.start_date;
      if (!s) return;
      // iterate days of leave that fall in this month
      let cur = new Date(s + 'T00:00:00');
      const end = new Date(e + 'T00:00:00');
      let guard = 0;
      while (cur <= end && guard < 400) {
        const ds = `${cur.getFullYear()}-${pad(cur.getMonth() + 1)}-${pad(cur.getDate())}`;
        if (ds.startsWith(monthPrefix)) {
          (map[ds] = map[ds] || []).push({
            name: nameMap[lv.personnel_id] || 'Personel',
            leave_type: lv.leave_type || 'diger',
          });
        }
        cur.setDate(cur.getDate() + 1);
        guard++;
      }
    });
    return map;
  }, [leaves, year, month, nameMap]);

  // Build calendar cells (Monday-first)
  const cells = useMemo(() => {
    const first = new Date(year, month - 1, 1);
    const startWeekday = (first.getDay() + 6) % 7; // Mon=0
    const daysInMonth = new Date(year, month, 0).getDate();
    const arr = [];
    for (let i = 0; i < startWeekday; i++) arr.push(null);
    for (let d = 1; d <= daysInMonth; d++) arr.push(d);
    while (arr.length % 7 !== 0) arr.push(null);
    return arr;
  }, [year, month]);

  const monthTotal = useMemo(() => Object.values(dayMap).reduce((s, arr) => s + arr.length, 0), [dayMap]);
  const todayStr = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-5xl max-h-[92vh] overflow-y-auto" data-testid="leave-calendar-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-blue-700">
            <CalendarDays className="w-5 h-5" /> İzin Takvimi
          </DialogTitle>
          <DialogDescription>Tüm personelin izin günleri aylık takvimde, izin türüne göre renkli gösterilir.</DialogDescription>
        </DialogHeader>

        {/* Kontrol çubuğu */}
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-2">
            <Button variant="outline" size="icon" onClick={() => shiftMonth(-1)} data-testid="leave-cal-prev"><ChevronLeft className="w-4 h-4" /></Button>
            <div className="font-bold text-lg text-slate-800 min-w-[160px] text-center" data-testid="leave-cal-month">{MONTHS_TR[month - 1]} {year}</div>
            <Button variant="outline" size="icon" onClick={() => shiftMonth(1)} data-testid="leave-cal-next"><ChevronRight className="w-4 h-4" /></Button>
          </div>
          <div className="text-sm text-slate-500">Bu ay toplam <strong className="text-blue-700">{monthTotal}</strong> izin günü</div>
        </div>

        {/* Renk açıklaması */}
        <div className="flex flex-wrap gap-3 text-xs text-slate-600">
          {Object.entries(LEAVE_STYLES).map(([k, v]) => (
            <span key={k} className="flex items-center gap-1"><span className={`w-3 h-3 rounded-full ${v.dot}`}></span>{v.label}</span>
          ))}
        </div>

        {/* Takvim */}
        <div className="border rounded-lg overflow-hidden mt-1">
          <div className="grid grid-cols-7 bg-slate-800 text-white text-xs font-semibold">
            {DAYS_TR.map((d, i) => (
              <div key={d} className={`px-2 py-2 text-center ${i >= 5 ? 'bg-slate-700' : ''}`}>{d}</div>
            ))}
          </div>
          <div className="grid grid-cols-7">
            {cells.map((d, idx) => {
              if (d === null) return <div key={idx} className="min-h-[92px] bg-slate-50 border-b border-r border-slate-100"></div>;
              const ds = `${year}-${pad(month)}-${pad(d)}`;
              const items = dayMap[ds] || [];
              const isWeekend = idx % 7 >= 5;
              const isToday = ds === todayStr;
              return (
                <div
                  key={idx}
                  className={`min-h-[92px] p-1.5 border-b border-r border-slate-100 ${isWeekend ? 'bg-rose-50/40' : 'bg-white'} ${isToday ? 'ring-2 ring-inset ring-blue-400' : ''}`}
                  data-testid={`leave-cal-day-${ds}`}
                >
                  <div className={`text-xs font-semibold mb-1 ${isToday ? 'text-blue-700' : 'text-slate-500'}`}>{d}</div>
                  <div className="space-y-1">
                    {items.slice(0, 4).map((it, i) => {
                      const st = styleFor(it.leave_type);
                      return (
                        <div key={i} className={`text-[10px] leading-tight px-1.5 py-0.5 rounded border truncate ${st.chip}`} title={`${it.name} — ${styleFor(it.leave_type).label}`}>
                          {it.name}
                        </div>
                      );
                    })}
                    {items.length > 4 && (
                      <div className="text-[10px] text-slate-400 px-1">+{items.length - 4} kişi</div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {loading && <p className="text-center text-sm text-slate-400 py-2">Yükleniyor...</p>}

        <div className="flex justify-end pt-1">
          <Button variant="outline" onClick={onClose} data-testid="leave-cal-close">Kapat</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default LeaveCalendarModal;
